// ai-review-draft-button.tsx — "✨ AI review" opt-in button for the review step.
// Explicit user action only (no autoRun) — review drafts cost more tokens than capture.
// Fills ALL three fields (chiêm nghiệm + điểm sáng + rào cản) via structured output.
// Hidden when AI is unavailable; caller controls via aiAvailable() guard in parent.

import { useState } from "react";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { todayIso } from "../lib/date";
import { createAiClient } from "../lib/ai/ai-runtime";
import { buildReviewSummary } from "../lib/ai/review-summary";
import { buildReviewRequest, mapReviewDraft } from "../lib/ai/review-prompt";
import type { ReviewDraft } from "../lib/ai/review-prompt";
import type { LifeOsApi } from "../lib/life-os-contract";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  api: LifeOsApi;
  scope: "week" | "month";
  /** Called with the AI-generated draft (3 fields) on success. User edits before saving. */
  onResult: (draft: ReviewDraft) => void;
}

type ButtonState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

// ── Component ─────────────────────────────────────────────────────────────────

export function AiReviewDraftButton({ api, scope, onResult }: Props) {
  const [state, setState] = useState<ButtonState>({ kind: "idle" });

  async function handleClick() {
    if (state.kind === "loading") return;
    setState({ kind: "loading" });
    try {
      const summary = buildReviewSummary(api.state, scope, todayIso());
      const client = createAiClient();
      const raw = await client.generateJson<ReviewDraft>(buildReviewRequest(summary));
      setState({ kind: "idle" });
      onResult(mapReviewDraft(raw));
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Không tạo được bản review — vui lòng thử lại.";
      setState({ kind: "error", message });
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={state.kind === "loading"}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-50 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50"
      >
        {state.kind === "loading" ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {state.kind === "loading" ? "AI đang viết…" : "AI review"}
      </button>

      {state.kind === "error" && (
        <div className="flex items-start gap-1.5 rounded-xl bg-rose-50 px-3 py-2 text-[11px] text-rose-600 dark:bg-rose-950/30 dark:text-rose-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{state.message}</span>
        </div>
      )}
    </div>
  );
}
